import { reactive } from 'vue'
import { apiFetch, getApiBase, getAuthToken } from './api'

export interface ManualExtractionResult {
  count: number
  at: string
  by: string
}

export interface MaintenanceManual {
  id: string
  name: string
  filename: string
  sizeBytes: number
  originalSizeBytes: number
  pageCount: number
  textOk: boolean
  lastExtractionAt: string
  uploadedBy: string
  createdAt: string
  projectIds: string[]
  /** Resultado do "enviar para o estagiário" por projeto (projectId → resultado) */
  extractions: Record<string, ManualExtractionResult>
}

export interface ExtractedRoutine {
  nome: string
  descricao: string
  checklist: string[]
  responsavelSugerido: string | null
  paginaReferencia: number | null
  responseTypes: Array<'foto' | 'texto' | 'check'>
}

export interface ExtractedGroup {
  periodicidade: 'diario' | 'semanal' | 'quinzenal' | 'mensal' | 'trimestral' | 'semestral' | 'anual'
  nome: string
  rotinas: ExtractedRoutine[]
}

export interface ExtractResult {
  ok: boolean
  manualId: string
  manualName: string
  mode: 'texto' | 'pdf'
  /** true quando o manual é tão extenso que a resposta da IA foi truncada
   *  (as rotinas exibidas são válidas, mas o final do manual pode ter ficado de fora) */
  truncated?: boolean
  grupos: ExtractedGroup[]
}

interface ManualsState {
  items: MaintenanceManual[]
  loading: boolean
  aiEnabled: boolean
  aiChecked: boolean
}

const state = reactive<ManualsState>({
  items: [],
  loading: false,
  aiEnabled: false,
  aiChecked: false
})

const load = async (projectId?: string) => {
  state.loading = true
  try {
    const url = projectId
      ? `/maintenance/manuals?projectId=${encodeURIComponent(projectId)}`
      : '/maintenance/manuals'
    state.items = await apiFetch<MaintenanceManual[]>(url)
  } finally {
    state.loading = false
  }
}

const refreshAiStatus = async () => {
  try {
    const r = await apiFetch<{ enabled: boolean }>('/maintenance/manuals-ai-status')
    state.aiEnabled = Boolean(r.enabled)
  } catch {
    state.aiEnabled = false
  } finally {
    state.aiChecked = true
  }
}

// Upload usa FormData (multipart) — não passa pelo apiFetch (que força JSON).
// clientText/clientPageCount: enviados quando o PDF foi rasterizado no navegador
// (compressão perde a camada de texto — o texto extraído no cliente mantém a IA barata).
const upload = async (
  file: File | Blob,
  filename: string,
  name: string,
  projectIds: string[],
  extra?: { clientText?: string; clientPageCount?: number }
): Promise<MaintenanceManual> => {
  const form = new FormData()
  form.append('file', file, filename)
  form.append('name', name)
  form.append('projectIds', JSON.stringify(projectIds))
  if (extra?.clientText) form.append('clientText', extra.clientText)
  if (extra?.clientPageCount) form.append('clientPageCount', String(extra.clientPageCount))
  const response = await fetch(`${getApiBase()}/maintenance/manuals`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: form
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(payload.error || 'Erro ao enviar o manual.')
  const manual = payload.manual as MaintenanceManual
  state.items = [manual, ...state.items.filter(m => m.id !== manual.id)]
  return manual
}

const update = async (id: string, data: { name?: string; projectIds?: string[] }) => {
  const r = await apiFetch<{ ok: boolean; manual: MaintenanceManual }>(
    `/maintenance/manuals/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(data) }
  )
  const idx = state.items.findIndex(m => m.id === id)
  if (idx >= 0) state.items.splice(idx, 1, r.manual)
  return r.manual
}

const remove = async (id: string) => {
  await apiFetch(`/maintenance/manuals/${encodeURIComponent(id)}`, { method: 'DELETE' })
  state.items = state.items.filter(m => m.id !== id)
}

// Abre o PDF autenticado em nova aba (blob URL, já que /uploads/manuais é bloqueado)
const openPdf = async (id: string) => {
  const response = await fetch(`${getApiBase()}/maintenance/manuals/${encodeURIComponent(id)}/content`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  })
  if (!response.ok) throw new Error('Erro ao abrir o manual.')
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

// "Enviar para o estagiário": IA interpreta o manual e devolve preview de rotinas
const extract = async (id: string): Promise<ExtractResult> => {
  return await apiFetch<ExtractResult>(
    `/maintenance/manuals/${encodeURIComponent(id)}/extract`,
    { method: 'POST', body: JSON.stringify({}) }
  )
}

// Registra o resultado do "enviar para o estagiário" após salvar o preview
const markExtracted = async (id: string, projectId: string, routinesCount: number) => {
  const r = await apiFetch<{ ok: boolean; manual: MaintenanceManual }>(
    `/maintenance/manuals/${encodeURIComponent(id)}/extraction-result`,
    { method: 'POST', body: JSON.stringify({ projectId, routinesCount }) }
  )
  const idx = state.items.findIndex(m => m.id === id)
  if (idx >= 0) state.items.splice(idx, 1, r.manual)
  return r.manual
}

// "Alguma dúvida?": pergunta sobre o manual com histórico rolante
const ask = async (
  id: string,
  question: string,
  history: Array<{ role: 'user' | 'model'; text: string }>
): Promise<string> => {
  const r = await apiFetch<{ ok: boolean; answer: string }>(
    `/maintenance/manuals/${encodeURIComponent(id)}/ask`,
    { method: 'POST', body: JSON.stringify({ question, history }) }
  )
  return r.answer
}

export const manualsStore = {
  state,
  load,
  refreshAiStatus,
  upload,
  update,
  remove,
  openPdf,
  extract,
  markExtracted,
  ask
}
